import React from 'react';
import useQuery from './UseQuery';

type UseQueryStateReturn<T> = [T, React.Dispatch<React.SetStateAction<T>>];
type QueryOptions<T> = {
	isArray?: boolean;
	deleteOnUnmount?: boolean;
	changeCallback?: (value: T) => void;
	noURLUpdate?: boolean;
	updateHistory?: boolean;
	popState?: boolean;
};

export default function useQueryState<T>(
	paramName: string,
	defaultValue: T,
	allowedValues?: T[],
	{ isArray = false, updateHistory = false, popState = true, ...options }: QueryOptions<T> = {},
): UseQueryStateReturn<T> {
	const query = useQuery({ key: paramName, updateHistory, popState });

	const getFromURL = () => {
		let queryValue = decodeURIComponent(query.get(paramName) ?? '');
		if (isArray && queryValue) {
			try {
				queryValue = JSON.parse(queryValue);
			} catch (e) {
				queryValue = '';
			}
		}
		if (!queryValue || (allowedValues?.length && !allowedValues.includes(queryValue as T))) {
			return defaultValue;
		}
		return queryValue as T;
	};

	const [value, _setValue] = React.useState<T>(getFromURL());
	const setValue = ((next: T | React.SetStateAction<T>) => {
		const nextValue = typeof next === 'function' ? (next as (prev: T) => T)(value) : next;
		_setValue(nextValue);

		const isEmpty = nextValue === false || nextValue === '' || nextValue === undefined || nextValue === null;
		// If the value is the default value, clear the URL parameter
		if (nextValue === defaultValue) {
			query.set(paramName, '');
			options.changeCallback?.(nextValue);
			return;
		}

		if (isEmpty || options.noURLUpdate) {
			if (options.noURLUpdate) {
				return;
			}
			query.set(paramName, '');
		} else {
			query.set(paramName, encodeURIComponent(isArray ? JSON.stringify(nextValue) : nextValue.toString()));
		}

		options.changeCallback?.(nextValue);
	}) as React.Dispatch<React.SetStateAction<T>>;

	return [value, setValue] as const;
}
